import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ArrowLeft, Download, Users, Filter, Calendar, School, GraduationCap, CheckCircle, XCircle } from 'lucide-react';


import { API_URL } from '../../../config/api';
const API = API_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function PollDetail({ user, onLogout }) {
  const { pollId } = useParams();
  const navigate = useNavigate();
  
  const [poll, setPoll] = useState(null);
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [responseFilter, setResponseFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchPollData();
    fetchPollStats();
  }, [pollId, responseFilter, schoolFilter, gradeFilter, startDate, endDate, sortBy, sortOrder]);

  const fetchPollData = async () => {
    try {
      const [pollRes, responsesRes] = await Promise.all([
        axios.get(`${API}/polls/${pollId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/polls/${pollId}/responses`, {
          params: {
            response_filter: responseFilter || undefined,
            school: schoolFilter || undefined,
            grade: gradeFilter || undefined,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            sort_by: sortBy,
            sort_order: sortOrder
          },
          headers: getAuthHeaders()
        })
      ]);

      setPoll(pollRes.data);
      setResponses(responsesRes.data.responses || []);
    } catch (error) {
      console.error('Error fetching poll data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPollStats = async () => {
    try {
      const response = await axios.get(`${API}/polls/${pollId}/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const exportToCSV = () => {
    if (responses.length === 0) return;

    const headers = ['Student Name', 'Email', 'School', 'Grade', 'Response', 'Date & Time'];
    const rows = responses.map(r => [
      r.user_name,
      r.user_email,
      r.school_name || 'N/A',
      r.grade || 'N/A',
      r.response,
      new Date(r.submitted_at).toLocaleString('en-IN')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `poll-${poll.poll_number}-responses.csv`;
    link.click();
  };

  const clearFilters = () => {
    setResponseFilter('');
    setSchoolFilter('');
    setGradeFilter('');
    setStartDate('');
    setEndDate('');
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!poll) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <p>Poll not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/polls')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Polls
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  Poll No. {poll.poll_number}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  poll.is_active 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {poll.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-800">{poll.question}</h1>
            </div>

            <button
              onClick={exportToCSV}
              disabled={responses.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500">
              <p className="text-gray-600 text-sm mb-1">Total Responses</p>
              <p className="text-3xl font-bold text-gray-800">{stats.total_responses}</p>
            </div>

            {poll.poll_type === 'yes_no' && (
              <>
                <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-gray-600 text-sm">Yes</p>
                  </div>
                  <p className="text-3xl font-bold text-gray-800">
                    {stats.responses_by_option.yes || 0}
                    <span className="text-lg text-gray-500 ml-2">
                      ({stats.total_responses > 0 
                        ? Math.round((stats.responses_by_option.yes || 0) / stats.total_responses * 100) 
                        : 0}%)
                    </span>
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-500">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <p className="text-gray-600 text-sm">No</p>
                  </div>
                  <p className="text-3xl font-bold text-gray-800">
                    {stats.responses_by_option.no || 0}
                    <span className="text-lg text-gray-500 ml-2">
                      ({stats.total_responses > 0 
                        ? Math.round((stats.responses_by_option.no || 0) / stats.total_responses * 100) 
                        : 0}%)
                    </span>
                  </p>
                </div>
              </>
            )}

            <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500">
              <p className="text-gray-600 text-sm mb-1">Unique Schools</p>
              <p className="text-3xl font-bold text-gray-800">{stats.unique_schools}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Response Filter */}
            {poll.poll_type === 'yes_no' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Response</label>
                <select
                  value={responseFilter}
                  onChange={(e) => setResponseFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            )}

            {/* School Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
              <input
                type="text"
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                placeholder="Search school..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Grade Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
              <input
                type="text"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                placeholder="e.g., 10th"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Sort Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="submitted_at">Submission Time</option>
                <option value="user_name">Student Name</option>
                <option value="grade">Class/Grade</option>
                <option value="school_name">School</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>

        {/* Responses Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Responses ({responses.length})
            </h2>
          </div>

          {responses.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              No responses found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      School
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {responses.map((response) => (
                    <tr key={response.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{response.user_name}</div>
                        <div className="text-sm text-gray-500">{response.user_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {response.school_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {response.grade || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          response.response === 'yes' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {response.response === 'yes' ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(response.submitted_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
